import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { NavBar } from "@/components/ui/tubelight-navbar";
  
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const navItems = [
    {
      name: 'Home',
      url: '/'
    }, 
    {
      name: 'Approach',
      url: '/approach'
    }, 
    {
      name: 'Insights',
      url: '/insights'
    },
    {
      name: 'Clients',
      url: '/clients'
    }
  ];

  // Get the active index based on current route
  const getActiveIndex = () => {
    if (location.pathname === '/') return 0;
    if (location.pathname === '/approach') return 1;
    if (location.pathname === '/insights') return 2;
    if (location.pathname === '/clients') return 3;
    return undefined;
  };
  
  useEffect(() => {
    const handleScroll = () => {
      // Only track scrolled state for visual effects on home page
      if (location.pathname === '/') {
        if (window.scrollY > 20) {
          setScrolled(true);
        } else {
          setScrolled(false);
        }
      } else {
        // Keep header transparent/unstyled on other pages
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Check for hash in URL after page load to handle direct navigation to sections
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.substring(1);
      const element = document.getElementById(sectionId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }, 300);
      }
    }
  }, [location]);
  
  const handleNavigation = (url: string, e: React.MouseEvent) => {
    e.preventDefault();

    // Handle navigation to different sections on home page
    if (url.startsWith('/#')) {
      // If we're already on home page, just scroll to the section
      if (window.location.pathname === '/') {
        const sectionId = url.substring(2);
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      } else {
        // If we're on a different page, navigate to home with the hash
        navigate(url);
      }
    } else {
      // Regular page navigation
      navigate(url);
    }
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };
  
  return (
    <header 
      style={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        boxShadow: scrolled ? '0 2px 10px rgba(0, 0, 0, 0.05)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(229, 229, 229, 0.5)' : 'none',
        padding: scrolled ? '0.5rem 0' : '1rem 0',
        transition: 'all 0.3s ease'
      }} 
      className="enhanced-header py-[25px]"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/align-logo.png" 
              alt="Align Product Studio Logo" 
              className="h-8 w-8 md:h-10 md:w-10 brand-logo"
            />
            <div className="text-darkText font-display font-semibold text-xl md:text-2xl">
              <span className="text-cyanPrimary">Align</span> Product Studio
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:block">
            <NavBar items={navItems} activeIndex={getActiveIndex()} />
          </div>

          <div className="hidden md:block">
            <a href="/#contact" onClick={e => handleNavigation('/#contact', e)}>
              <Button className="bg-cyanPrimary hover:bg-cyanLight text-white rounded-xl shadow-soft hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
                Get in Touch
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-darkText" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bg-white/95 backdrop-blur-lg p-4 shadow-card z-50 border-b border-grayMedium/20">
          <nav className="flex flex-col space-y-4">
            {navItems.map((item, index) => (
              <a 
                key={index} 
                href={item.url} 
                className="text-darkText hover:text-cyanPrimary transition-colors p-2 animated-underline" 
                onClick={e => handleNavigation(item.url, e)}
              >
                {item.name}
              </a>
            ))}
            <a 
              href="/#contact" 
              className="bg-cyanPrimary text-white p-3 rounded-lg text-center hover:bg-cyanLight transition-all duration-300" 
              onClick={e => handleNavigation('/#contact', e)}
            >
              Get in Touch
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
